import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, Signal, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom } from 'rxjs';
import { v4, v7 } from 'uuid';
import type { AIChatContextGuardStatus, AIInterviewAnswer, AIInterviewResponse } from '@jms/model';
import type { AIDurablePendingSteeringEntry } from '@jms/model';
import { AIService } from '@workbuddy/ai/core/ai.service';
import { AiUsageLimitService } from '@workbuddy/ai/core';
import { NativeWorkbuddyAgentChatAdapter } from './native.workbuddy.agent.chat.adapter';
import { DurableWorkbuddyAgentChatAdapter } from './durable.workbuddy.agent.chat.adapter';
import { DurableAgentTransportService } from './durable.agent.transport.service';
import { isDurableRunsEnabled } from './durable.runs.flag';
import type { AgentChatAdapter } from './agent.chat.adapter';
import { AgentUtility } from '../agent.utility';
import { appendToComposerText } from '../composer.text';
import { AgentPanelService, NEW_CHAT_DRAFT_KEY } from './agent.panel.service';
import { AgentRevealService } from './agent.reveal.service';
import { BackgroundStreamManager } from './background.stream.manager';
import type { AgentModelTier, AnyPart, CardAction, PendingFile, InterviewCardActionMeta, ThreadLoadFailure } from '../agent.types';
import { coerceThreadModelTier, readPersistedModelTier } from '../agent.types';
import type { UIMessage } from 'ai';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Delay before fetching thread title after first message (allows Mastra generateTitle to complete) */
const TITLE_FETCH_DELAY_MS = 2000;

/** Max poll attempts for the generated title — generateTitle is an async LLM call that can take longer than one delay */
const TITLE_FETCH_MAX_ATTEMPTS = 6;

/** Maximum number of threads to keep in the in-memory message cache */
const MAX_CACHED_THREADS = 20;

/**
 * How long to wait before re-attempting a thread whose message load failed.
 *
 * A failed load leaves the foreground empty, which is exactly the condition the popup uses to decide
 * a thread needs loading — so without a cooldown every panel open/close re-fired the same failing
 * request. When the failure is a rate limit, that feedback loop makes the rate limit worse.
 */
const THREAD_LOAD_RETRY_COOLDOWN_MS = 10_000;

/**
 * Chat service for Agent V2.
 * Wraps NativeWorkbuddyAgentChatAdapter to provide a clean API for chat components.
 * Components never touch the adapter directly — they interact through this service's signals and methods.
 *
 * Provided at component level (one instance per chat container), NOT providedIn root.
 */
@Injectable()
export class AgentChatService {

  private readonly aiService = inject(AIService);
  private readonly usageLimits = inject(AiUsageLimitService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly panelService = inject(AgentPanelService);
  private readonly backgroundManager = inject(BackgroundStreamManager);
  private readonly revealService = inject(AgentRevealService);
  private readonly durableTransport = inject(DurableAgentTransportService);

  // THE switch between runtimes: read once per service so a chat never changes adapter mid-life.
  // On: runs are server-owned and the browser subscribes. Off: today's request-bound adapter,
  // untouched. Every other branch in this file keys off this same field.
  private readonly durableEnabled = isDurableRunsEnabled();

  // ─── Configuration (set by chat.container via configure()) ──────────────────
  private $mode!: Signal<'fullscreen' | 'popup'>;
  private $currentJobId!: Signal<string | undefined>;
  private $currentWorkOrderId!: Signal<string | undefined>;
  private $interactionMode!: Signal<'interactive' | 'automation'>;

  // ─── Chat adapter ──────────────────────────────────────────────────────────
  /** Signal holding the current adapter — effects read $chat() to auto re-track on swap. */
  private readonly $chat = signal<AgentChatAdapter>(null!);
  /** Untracked read for imperative code — avoids polluting calling effects' dependencies. */
  private get chat(): AgentChatAdapter { return untracked(() => this.$chat()); }
  /** Bumped by every switch to another chat or to New chat, so an awaited send can tell the chat it was written for has gone. */
  private threadSwitches = 0;
  /**
   * The thread a brand-new durable chat is creating. Every send made before it exists waits on this
   * one creation: each starting its own put the messages of one chat into separate threads.
   */
  private pendingThreadCreation: Promise<string | null> | null = null;
  /** Set once the container is destroyed: a send still preparing must not dispatch through an adapter nothing holds. */
  private disposed = false;
  /** Counts Stop presses, so a send still preparing (thread creation, tier lookup) can tell it was stopped before it left. */
  private stopPresses = 0;

  /** The current adapter when it is the durable one, else null. */
  private get durableChat(): DurableWorkbuddyAgentChatAdapter | null {

    const chat = this.chat;
    return chat instanceof DurableWorkbuddyAgentChatAdapter ? chat : null;
  }
  private readonly threadMessageCache = new Map<string, UIMessage[]>();
  private readonly threadCacheOrder: string[] = [];
  // Per-thread model tier, resolved once from thread metadata and then reused. Clicking
  // back and forth between the same chats must NOT re-fetch the thread each time — that
  // redundant per-switch GET is what bursts the API into 429s. A cache hit publishes the
  // tier synchronously and skips the network call entirely.
  private readonly threadModelTierCache = new Map<string, AgentModelTier | null>();
  // In-flight tier lookups, so the two callers (thread switch and send) share one request instead of
  // racing two. The cache above is only written when a response lands, so without this a send issued
  // straight after switching to a cold thread fires a second identical GET.
  private readonly threadModelTierRequests = new Map<string, Promise<AgentModelTier | null>>();
  // Threads confirmed to hold no messages. Tracked separately from threadMessageCache because that
  // cache is LRU-evicted — recording emptiness here rather than as an empty cache entry means an
  // evicted thread WITH messages still re-fetches, while a genuinely empty one never does.
  private readonly knownEmptyThreadIds = new Set<string>();
  // When a thread's message load last failed, keyed by thread id — see THREAD_LOAD_RETRY_COOLDOWN_MS.
  private readonly failedThreadLoads = new Map<string, ThreadLoadFailure>();
  // Threads whose messages are being fetched right now. An in-flight load leaves the foreground
  // empty, which is indistinguishable from "not loaded yet" to the caller, so this is what stops a
  // slow response being re-requested every time the panel is toggled. A signal because it also
  // drives $threadLoading, which holds sends until the open thread's history has landed.
  private readonly loadingThreadIds = signal<ReadonlySet<string>>(new Set());
  private titleFetchTimer: ReturnType<typeof setTimeout> | null = null;

  // ─── Reasoning state ───────────────────────────────────────────────────────
  private readonly manuallyExpandedBlocks = new Set<string>();

  // ─── Public signals (readonly to consumers) ────────────────────────────────
  public readonly $threadId: WritableSignal<string | null> = signal<string | null>(null);
  public readonly $allSessionBlobUrls: WritableSignal<Record<string, string>> = signal<Record<string, string>>({});
  public readonly $cardActions: WritableSignal<Record<string, InterviewCardActionMeta>> = signal<Record<string, InterviewCardActionMeta>>({});
  public readonly $collapsedActivityBlocks: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  // The model tier the ACTIVE thread was started on, read back from its stored metadata. null means
  // "no thread-specific tier" — either a brand-new empty chat or a pre-feature thread with no tier
  // recorded — in which case the composer falls back to the user's persisted default. Set on thread
  // restore/switch (from getThread metadata) and on the first send of a new chat; cleared on New Chat.
  public readonly $activeThreadModelTier: WritableSignal<AgentModelTier | null> = signal<AgentModelTier | null>(null);

  public readonly $streamingError = signal<string | null>(null);
  /** Prevents the composer from submitting into a missing or inaccessible conversation. */
  private readonly _threadUnavailable = signal(false);
  public readonly $threadUnavailable = this._threadUnavailable.asReadonly();
  /**
   * True while the open thread's saved history is still loading. Sends wait on it: until the load
   * lands, the thread is not yet known to exist or be accessible, so a turn could go into a missing
   * or denied conversation. Read by the composer to hold Send, and by sendMessage as the real guard.
   */
  public readonly $threadLoading = computed(() => {

    const threadId = this.$threadId();
    return !!threadId && this.loadingThreadIds().has(threadId);
  });

  // ─── Durable runtime signals (empty/null on the request-bound path) ──────────
  public readonly $durableEnabled = signal(this.durableEnabled);
  /** Steering sent while the agent worked, queued above the composer until the loop weaves it in. */
  public readonly $pendingSteering = signal<AIDurablePendingSteeringEntry[]>([]);
  /** Decide-first copy while a card waits; the composer is disabled until approve, decline or stop. */
  public readonly $decideFirstMessage = signal<string | null>(null);
  /** Calm notice from a durable route (temporarily unavailable, attached to another window's run). */
  public readonly $durableNotice = signal<string | null>(null);
  /** Unconsumed steering text Stop handed back; the container puts it into the composer and acknowledges. */
  public readonly $composerRestoreText = signal<string | null>(null);
  /**
   * Files of a send that never travelled (refused, or unable to join a running run), held by the open
   * chat's adapter for its composer. Read from whichever adapter is open, so files refused in the
   * background come back when that chat is reopened, not in the chat that happened to be on screen.
   */
  public readonly $heldAttachments = signal<PendingFile[]>([]);
  /** A Stop the server could not confirm: the composer shows Stopping while it keeps checking. */
  public readonly $stopPending = signal(false);
  private readonly _threadLoadError = signal<string | null>(null);
  public readonly $threadLoadError = this._threadLoadError.asReadonly();

  private readonly _expandedTools = signal(new Set<string>());
  public readonly $expandedTools = this._expandedTools.asReadonly();

  // These are initialized with safe defaults and updated in initChat() when the adapter is created
  public readonly $messages = signal<UIMessage[]>([]);
  public readonly $isStreaming = signal(false);
  public readonly $contextGuardStatus = signal<AIChatContextGuardStatus | null>(null);
  /** The model provider itself refused (its own quota/billing/capacity), not our tenancy cap. */
  public readonly $providerUnavailable = signal(false);
  public readonly $threadTitleUpdate = signal<{ threadId: string; title: string } | null>(null);
  public readonly $lastMessageIsUser = computed(() => {

    const m = this.$messages();
    return m.length > 0 && m[m.length - 1].role === 'user';
  });

  /**
   * Configure the service with context signals from the chat container.
   * Must be called before initChat().
   */
  public configure(config: { $mode: Signal<'fullscreen' | 'popup'>; $currentJobId: Signal<string | undefined>; $currentWorkOrderId: Signal<string | undefined>; $interactionMode: Signal<'interactive' | 'automation'> }): void {

    this.$mode = config.$mode;
    this.$currentJobId = config.$currentJobId;
    this.$currentWorkOrderId = config.$currentWorkOrderId;
    this.$interactionMode = config.$interactionMode;
  }

  /**
   * Initialize the chat adapter and set up all reactive effects.
   * Call after configure() and optionally pass a threadId to restore.
   */
  public initChat(threadId?: string): void {

    this.$chat.set(this.createAdapter());

    // Sync public signals from the adapter via effects.
    // $chat() is tracked — effects auto re-track when adapter is swapped via $chat.set().
    effect(() => {

      const msgs = this.$chat().messages;
      this.$messages.set(msgs);
    }, { injector: this.injector });
    effect(() => {

      const status = this.$chat().status;
      this.$isStreaming.set(status === 'streaming' || status === 'submitted');
    }, { injector: this.injector });
    // Sync streaming error from adapter into a dedicated signal
    effect(() => {

      const error = this.$chat().error;
      this.$streamingError.set(error?.message ?? null);
    }, { injector: this.injector });
    effect(() => {

      this.$contextGuardStatus.set(this.$chat().contextGuardStatus);
    }, { injector: this.injector });
    effect(() => {

      this.$providerUnavailable.set(this.$chat().providerUnavailable);
    }, { injector: this.injector });
    // A send refused by the usage-limit door check (429 from a stale browser) is not an error:
    // the adapter rolled the turn back silently; re-reading the status here flips the composer
    // into the blocked notice, which explains what happened and when AI resumes.
    effect(() => {

      if (this.$chat().usageLimitRejections > 0) void this.usageLimits.refresh();
    }, { injector: this.injector });
    effect(() => {

      const chat = this.$chat();
      const durable = chat instanceof DurableWorkbuddyAgentChatAdapter ? chat : null;
      this.$pendingSteering.set(durable?.pendingSteering ?? []);
      this.$decideFirstMessage.set(durable?.decideFirstMessage ?? null);
      this.$durableNotice.set(durable?.notice ?? null);
      this.$composerRestoreText.set(durable?.composerRestore ?? null);
      this.$heldAttachments.set(durable?.heldUploads ?? []);
      this.$stopPending.set(durable?.stopPending ?? false);
    }, { injector: this.injector });

    // Set up effects
    this.revealService.init(this.$chat);
    this.setupAutoCollapseActivityBlocksEffect();
    this.setupProvisionalTabEffect();
    this.setupTitleFetchOnTurnEndEffect();

    // Restore thread if provided
    if (threadId) {
      this.$threadId.set(threadId);
      this.loadActiveThreadModelTier(threadId);
      this.aiService.getThreadMessages(AgentUtility.AGENT_ID, threadId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          this.chat.messages = AgentUtility.convertToUIMessages(response.uiMessages ?? []);
          this.syncSignalsFromAdapter();
          this.attachDurable(threadId);
        },
        error: (err) => console.error('[AgentChat] Failed to restore thread:', err),
      });
    }
  }

  /**
   * Durable path only: after the messages are on screen, read the thread's status and subscribe so
   * a run in progress (from any window) streams in and queued cards show. The request-bound path
   * has nothing to attach to; a thread only moves when this browser sends.
   */
  private attachDurable(threadId: string): void {

    const durable = this.durableChat;
    if (!durable || durable.attachedThreadId === threadId) return;
    void durable.attach(threadId).then(() => this.syncSignalsFromAdapter());
  }

  /**
   * Durable path only: a transcript reopened from the cache (or a thread last known to be empty) is
   * shown at once, then re-read from storage before attaching. A durable thread moves without this
   * window: another window can finish a turn in it while it sits idle here, and the subscription only
   * streams runs still going, so that turn would never appear. A window still attached to the thread
   * is live and keeps its view.
   */
  private revalidateCachedDurableThread(threadId: string): void {

    const durable = this.durableChat;
    if (!durable || durable.attachedThreadId === threadId) return;
    void durable
      .hydrateAndAttach(threadId)
      .then(messages => {
        if (messages.length > 0) this.knownEmptyThreadIds.delete(threadId);
        if (this.$threadId() === threadId && this.durableChat === durable) this.syncSignalsFromAdapter();
      })
      .catch(error => {
        // Storage could not be read: keep the cached transcript and still follow the thread live.
        console.error('[AgentChat] Failed to revalidate a cached thread:', error);
        if (this.$threadId() === threadId && this.durableChat === durable) this.attachDurable(threadId);
      });
  }

  /**
   * Clean up the chat adapter and all pending work.
   */
  public dispose(): void {

    // Leaving the chat this way is a switch too: a first send still waiting for its thread, or a send
    // waiting for its tier, keeps its words in the draft instead of starting a run nothing watches.
    this.disposed = true;
    this.threadSwitches += 1;
    this.pendingThreadCreation = null;
    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this.revealService.reset();
    this.detachCurrentStream();
    // An adapter that was not handed to the background dies with this container; release what it
    // holds in the browser (the durable subscription) without touching the run.
    this.chat?.dispose();
  }

  /**
   * Detach the current adapter to BackgroundStreamManager so its live state survives this container.
   * Extracted from switchThread/createNewThread/dispose to eliminate duplication.
   *
   * A paused write-tool approval is `idle` (not streaming), but it holds the in-memory runId needed to
   * resume — so it is preserved here alongside active streams. Without this, destroying the container
   * (Automations swap, fullpage nav-away/minimize) would drop the pending approval and force a lossy
   * cold reload; keeping the adapter lets switchThread reattach the exact card, runId intact.
   */
  private detachCurrentStream(): void {

    // A send still waiting for the route's answer is kept too, idle or not: its refusal hands words
    // back through this adapter, and a reset adapter would drop that answer unseen.
    if (!this.$isStreaming() && !this.chat.hasPendingApproval && !this.durableChat?.sendPending) return;
    const threadId = this.$threadId();
    if (threadId) {
      // Soft detach: the request-bound adapter keeps its fetch alive in the background, the durable
      // adapter keeps only its subscription. Neither is a Stop; navigation never ends a run.
      this.backgroundManager.detach(threadId, this.chat);
      this.$chat.set(this.createAdapter());
    } else {
      void this.chat.stop();
    }
  }

  /**
   * Cache current messages and detach the streaming adapter to background.
   * Called by chat.container when the popup panel is minimized.
   */
  public detachToBackground(): void {

    const threadId = this.$threadId();
    // A durable chat whose tab was just closed is let go instead of pooled. Closing released what
    // the pool held for it, but the active chat only reaches the pool here, after that release had
    // already missed it, and stayed connected until its run ended. Its run lives on the server, and
    // the half-written transcript is not cached, so reopening it reads storage. A send still waiting
    // for its answer keeps the adapter until then, since a refusal hands its words back through it.
    const durable = this.durableChat;
    if (threadId && durable && !this.panelService.$openTabs().some(tab => tab.threadId === threadId)) {
      this.forgetCachedThread(threadId);
      if (durable.sendPending) this.backgroundManager.detach(threadId, durable, { closing: true });
      else durable.dispose();
      this.$chat.set(this.createAdapter());
      return;
    }
    if (threadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(threadId, this.chat.messages);
    }
    this.detachCurrentStream();
  }

  /**
   * Rebind public signals from the current chat adapter.
   * Called after setting a new adapter to ensure immediate signal sync.
   */
  private syncSignalsFromAdapter(): void {

    this.$messages.set(this.chat.messages);
    this.$isStreaming.set(this.chat.status === 'streaming' || this.chat.status === 'submitted');
    this.$contextGuardStatus.set(this.chat.contextGuardStatus);
    this.$providerUnavailable.set(this.chat.providerUnavailable);
    this.rebuildThreadState();
  }

  /** "Try again" on the provider-unavailable notice: restore the composer so the user can resend. */
  public dismissProviderUnavailable(): void {

    this.chat.clearProviderUnavailable();
    this.$providerUnavailable.set(false);
  }

  // ─── Messaging ─────────────────────────────────────────────────────────────

  /**
   * Send a message with optional file attachments and metadata.
   */
  // `modelTier` defaults to the user's persisted composer choice so callers that don't pass it
  // explicitly (e.g. retry) still honour the selected tier. Only the tier string ever travels —
  // never a model name.
  public async sendMessage(text: string, files?: PendingFile[], metadata?: Record<string, unknown>, modelTier: AgentModelTier = readPersistedModelTier()): Promise<void> {

    if (this.$threadUnavailable() || this.$threadLoading()) return;

    if (this.$interactionMode() === 'interactive' && this.$contextGuardStatus()?.band === 'blocked') {
      return;
    }

    // Detect first turn before setting threadId. A send while that first thread is still being
    // created joins the creation instead of starting another, and then goes as guidance.
    const isFirstTurn = !this.$threadId();
    const stopsBefore = this.stopPresses;
    const joinedCreation = isFirstTurn ? this.pendingThreadCreation : null;
    const createsThread = isFirstTurn && !joinedCreation;

    // Build FileUIPart[] from ready files with blob URLs
    const readyFiles = files?.filter(f => f.status === 'ready') ?? [];
    // Duplicate filename into providerMetadata.workbuddy.filename so it survives Mastra V2's
    // memory round-trip — V2 strips the top-level `filename` field on file parts during
    // persistence/replay, but keeps providerMetadata. Both jms-web and jms-ai fall back to
    // this field when reading file parts from history.
    const fileParts = readyFiles
      .filter(f => f.blobUrl)
      .map(f => {
        const filename = `att_${f.id}__${f.file.name}`;
        return { type: 'file' as const, url: f.blobUrl!, mediaType: f.file.type, filename, providerMetadata: { workbuddy: { filename } } };
      });

    // Merge new blob URLs into cumulative session map
    const newBlobUrls: Record<string, string> = {};
    for (const f of readyFiles) {
      if (f.blobUrl) newBlobUrls[f.id] = f.blobUrl;
    }
    if (Object.keys(newBlobUrls).length > 0) {
      this.$allSessionBlobUrls.update(prev => ({ ...prev, ...newBlobUrls }));
    }

    // Rebuild file list for generative UI cards
    this.rebuildThreadState();

    // Build a UIMessage for the native adapter
    const parts: Array<{ type: string; text?: string; url?: string; mediaType?: string; filename?: string; providerMetadata?: { workbuddy: { filename: string } } }> = [];
    if (text) {
      parts.push({ type: 'text', text });
    }
    for (const fp of fileParts) {
      parts.push(fp);
    }
    const userMessage: UIMessage = { id: v4(), role: 'user', parts: parts as UIMessage['parts'] };
    // The adapter keeps the composer's upload records with the request: a refused send hands them
    // back and Retry resends them, even after this service is gone and a new view reattaches it.
    this.durableChat?.rememberUploads(userMessage.id, readyFiles);

    // On screen FIRST, before the round trips the send needs. A first turn on the durable path has
    // to create its thread before it can send, and that is the wait the user feels most: the
    // transcript behind it is empty. The adapter's send claims this pair instead of painting again.
    // A send while the agent works is steering: it has no reply bubble of its own (it waits as a
    // queued bubble at the end of the transcript), and painting a pair here would make the previous
    // activity block stop being the trailing one and collapse before the message was woven in.
    const durable = this.durableChat;
    if (durable && !this.$isStreaming()) {
      durable.beginTurn(userMessage);
      this.syncSignalsFromAdapter();
    }

    // Generate a thread ID on first message so the backend can persist from the start. The durable
    // send route only accepts a thread that already exists (ownership is checked before the lease),
    // so on that path the thread is created first and its id adopted.
    if (isFirstTurn) {
      const switchesBefore = this.threadSwitches;
      const creation = joinedCreation ?? this.startThreadCreation(modelTier);
      const newThreadId = await creation;
      // The user moved to another chat while this one was being created: adopting the new id now
      // would send this message on top of the chat they opened. The words go back to the new chat's
      // draft, where they were typed; the adapter now serves the chat that was opened, so handing them
      // back through it put them in that chat's composer and Retry.
      if (this.threadSwitches !== switchesBefore) {
        this.handBackToNewChat(userMessage, text);
        return;
      }
      // Stop pressed meanwhile means the same for a message that joined the creation; the first
      // message itself was already handed back by the Stop.
      const stopped = this.stopPresses !== stopsBefore;
      if (stopped && joinedCreation) {
        durable?.handBackUnsent(userMessage);
        this.syncSignalsFromAdapter();
        return;
      }
      if (stopped) return;
      if (!newThreadId) {
        // Nothing was sent, so the message must not be left sitting in the transcript as if it had
        // been — and it must not vanish either: the words go back to the composer to try again.
        // A message that joined the creation painted nothing, so only its words go back. The error is
        // raised only here, after the switch check above: raised by the failed creation itself, it
        // landed in whichever chat was open by then, whose Retry resent that chat's own last message.
        if (joinedCreation) durable?.handBackUnsent(userMessage);
        else durable?.abandonTurn({ restore: true });
        this.$streamingError.set('Could not start this chat. Please try again.');
        this.syncSignalsFromAdapter();
        return;
      }
      if (createsThread) {
        // The composer's draft is keyed by thread, and publishing the id moves it from the new-chat
        // key to this thread's. Anything typed while the thread was being created moves with it,
        // instead of being replaced by the new thread's empty draft.
        const typedMeanwhile = this.panelService.composerDraftFor(NEW_CHAT_DRAFT_KEY);
        if (typedMeanwhile) {
          this.panelService.setComposerDraft(newThreadId, typedMeanwhile);
          this.panelService.clearComposerDraft(NEW_CHAT_DRAFT_KEY);
        }
        this.$threadId.set(newThreadId);
        // Mirror the chosen tier locally so the composer locks to it immediately. The backend stamps
        // the same tier onto the new thread's metadata on this first turn; seeding the cache here means
        // switching away and back never triggers a getThread for a thread we already know the tier of.
        this.$activeThreadModelTier.set(modelTier);
        this.threadModelTierCache.set(newThreadId, modelTier);
        this.panelService.markTitlePending(newThreadId);
        // Show the start of the message as the title straight away; the generated title replaces it when it lands.
        const provisionalTitle = AgentUtility.provisionalThreadTitle(text);
        if (provisionalTitle) this.$threadTitleUpdate.set({ threadId: newThreadId, title: provisionalTitle });
      }
    }

    const threadId = this.$threadId()!;

    // This thread is about to hold a message, so it is no longer one we can skip loading.
    this.knownEmptyThreadIds.delete(threadId);

    // Tier that travels with the turn. A NEW chat uses the composer's pick (already seeded into the
    // cache above). An EXISTING thread's tier was fixed at creation and stored in metadata; the
    // composer only mirrors it and can transiently read 'default' before a cold switch/restore
    // resolves getThread -- so resolve the authoritative tier here and never let a fast send override it.
    const effectiveModelTier = createsThread ? modelTier : ((await this.resolveThreadModelTier(threadId)) ?? modelTier);

    // The lookup above can outlast a switch to another conversation, a fresh history load of this
    // one, or a denial landing for it. Each means the conversation this turn was written for is no
    // longer the open, accessible one, so dispatching would send it into a stale or denied thread.
    if (this.disposed || this.$threadId() !== threadId || this.$threadUnavailable() || this.$threadLoading()) {
      this.handBackStaleTurn(threadId, userMessage, text, durable);
      return;
    }

    // Stop pressed while the tier was being looked up: nothing has left, so nothing may now. The
    // adapter's own Stop has already settled the screen; the words go back to the composer. Checked
    // after the conversation is known to be the same one: once the user has moved on, the adapter
    // may serve the chat they opened, and the words belong in the left chat's draft instead.
    if (this.stopPresses !== stopsBefore) {
      durable?.abandonTurn();
      durable?.handBackUnsent(userMessage);
      this.syncSignalsFromAdapter();
      return;
    }

    await this.chat.sendTurn({
      userMessage,
      threadId,
      threadIsNew: createsThread,
      interactionMode: this.$interactionMode(),
      modelTier: effectiveModelTier,
      currentJobId: this.$currentJobId(),
      currentWorkOrderId: this.$currentWorkOrderId(),
      attachmentBlobUrls: this.$allSessionBlobUrls(),
    });
  }

  /**
   * A first message left before its thread existed goes back to the new chat it was typed in. When
   * that new chat is what the window shows now, the words go through the adapter's composer hand-back,
   * which puts them into the visible composer; a saved draft alone is only read when the composer
   * switches conversation, so it stayed hidden and the next keystroke overwrote it.
   */
  private handBackToNewChat(userMessage: UIMessage, text: string): void {

    const durable = this.durableChat;
    if (durable && !this.$threadId() && !this.disposed) {
      durable.handBackUnsent(userMessage);
      this.syncSignalsFromAdapter();
      return;
    }
    if (text) this.panelService.setComposerDraft(NEW_CHAT_DRAFT_KEY, appendToComposerText(this.panelService.composerDraftFor(NEW_CHAT_DRAFT_KEY), text));
  }

  /**
   * A turn that will not be sent because its chat is no longer the open, accessible one: its words go
   * back to that chat's composer.
   *
   * After a switch the composer shows another chat and the turn's adapter may already be in the
   * background pool, where a hand-back was invisible and was then dropped when the pool released the
   * adapter. So the words go into the left chat's saved draft instead, and a pooled adapter the turn
   * alone had made look busy is dropped rather than reported as a finished run.
   */
  private handBackStaleTurn(threadId: string, userMessage: UIMessage, text: string, durable: DurableWorkbuddyAgentChatAdapter | null): void {

    if (this.$threadId() === threadId && !this.disposed) {
      durable?.abandonTurn();
      durable?.handBackUnsent(userMessage);
      this.syncSignalsFromAdapter();
      return;
    }
    // An adapter reused for the chat now open lost this turn's paint when it was reset, and any turn
    // it shows now is that chat's; only an adapter that went to the pool still holds this one.
    if (durable && durable !== this.chat) {
      durable.abandonTurn();
      if (durable.status === 'idle') this.backgroundManager.cancel(threadId, durable);
    }
    if (text) this.panelService.setComposerDraft(threadId, appendToComposerText(this.panelService.composerDraftFor(threadId), text));
  }

  /**
   * Stop the active stream. On the durable path this ends the run on the server from any window and
   * hands unconsumed steering back through `$composerRestoreText`.
   */
  public async stopStreaming(): Promise<void> {

    // The first message of a new chat is still waiting for its thread, so there is no run to stop
    // yet: the message is taken back now and nothing goes once the thread exists.
    this.stopPresses += 1;
    const creation = this.pendingThreadCreation;
    if (creation) {
      this.pendingThreadCreation = null;
      this.durableChat?.abandonTurn({ restore: true });
      this.syncSignalsFromAdapter();
      return;
    }
    await this.chat.stop();
  }

  /** The container has put the restored steering text into the composer. */
  public acknowledgeComposerRestore(): void {

    this.durableChat?.acknowledgeComposerRestore();
    this.$composerRestoreText.set(null);
  }

  /**
   * The send the server refused, as the composer and Retry need it: its words and the files that
   * went with it, or null. Read straight from the adapter, so a caller reacting to the send's own
   * promise sees it before the next change-detection pass.
   */
  public get failedSend(): { text: string; files: PendingFile[] } | null {

    const durable = this.durableChat;
    const failed = durable?.failedSend;
    if (!durable || !failed) return null;
    return { text: failed.text, files: durable.uploadsFor(failed.requestId) };
  }

  /** The container has put the held files back into the composer. */
  public acknowledgeHeldAttachments(): void {

    this.durableChat?.acknowledgeAttachmentsHeld();
    this.$heldAttachments.set([]);
  }

  public dismissDurableNotice(): void {

    this.durableChat?.clearNotice();
    this.$durableNotice.set(null);
  }

  /**
   * Starts creating the first thread of a new chat: a local id on the request-bound path, the
   * durable thread otherwise, which every send made before it exists then shares.
   */
  private startThreadCreation(modelTier: AgentModelTier): Promise<string | null> {

    if (!this.durableEnabled) return Promise.resolve(v7());
    const creation = this.createDurableThread(modelTier);
    this.pendingThreadCreation = creation;
    void creation.finally(() => {
      if (this.pendingThreadCreation === creation) this.pendingThreadCreation = null;
    });
    return creation;
  }

  /**
   * Creates the thread the durable send route requires; null when it cannot. The caller surfaces the
   * failure, because only it knows whether the chat that asked is still the one on screen.
   */
  private async createDurableThread(modelTier: AgentModelTier): Promise<string | null> {

    try {
      const thread = await firstValueFrom(this.aiService.createThread(AgentUtility.AGENT_ID, { metadata: { modelTier } }));
      return thread.id;
    } catch (error) {
      console.error('[AgentChat] Failed to create a thread for the durable runtime:', error);
      return null;
    }
  }

  // ─── Thread Management ─────────────────────────────────────────────────────

  /**
   * Switch to an existing thread, caching the current one.
   */
  public async switchThread(threadId: string): Promise<void> {

    this.threadSwitches += 1;
    // A first thread still being created belongs to the chat being left; a send in the next chat must not join it.
    this.pendingThreadCreation = null;
    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this._threadLoadError.set(null);
    this._threadUnavailable.set(false);
    this.detachCurrentStream();

    // Returning to the thread the durable adapter is ALREADY watching reuses its live subscription,
    // and the guards that keep that run rendering once have to survive with it — see the adapter's
    // `resetUnlessAttachedTo`. The popup lands here on the SAME thread whenever its foreground is
    // empty. A genuinely different thread is reset either here or by the adapter's own `attach()`.
    const durable = this.durableChat;
    if (durable) durable.resetUnlessAttachedTo(threadId);
    else this.chat.resetTransientState();

    // Save current thread's live messages to cache
    const prevThreadId = this.$threadId();
    if (prevThreadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(prevThreadId, this.chat.messages);
    }

    this.$threadId.set(threadId);
    this.$allSessionBlobUrls.set({});
    // Resolve the thread's stored tier so the composer locks to whatever this chat ran on. Runs for
    // every branch below (reattach / warm cache / cold load) since the tier lives in thread metadata.
    this.loadActiveThreadModelTier(threadId);

    // Check if the target thread is running in background — reattach it
    const reattached = this.backgroundManager.reattach(threadId);

    // A turn still running or waiting on an interview polls when it ends (setupTitleFetchOnTurnEndEffect), so only re-poll a finished one.
    const turnFinished = !reattached || AgentChatService.isTurnFinished(reattached);
    if (this.panelService.isTitlePending(threadId) && turnFinished) this.fetchThreadTitle(threadId);

    if (reattached) {
      // A pooled adapter takes over: the outgoing one is dropped rather than reused, so whatever it
      // still holds in the browser is released here even when the reset above was skipped.
      this.chat.resetTransientState();
      this.$chat.set(reattached);
      this.backgroundManager.markRead(threadId);
      this.syncSignalsFromAdapter();
      return;
    }

    // A finished background chat is released from the pool but keeps its unread mark until it is opened.
    this.backgroundManager.markRead(threadId);
    // Its transcript was cached when it left and the released adapter held the rest of the reply, so
    // the cache would reopen it half-written; storage holds the finished reply.
    if (this.backgroundManager.takeReleased(threadId)) this.forgetCachedThread(threadId);

    // Warm cache hit: restore live messages (preserves pending approvals)
    const cached = this.threadMessageCache.get(threadId);
    if (cached) {
      this.chat.messages = cached;
      this.syncSignalsFromAdapter();
      this.revalidateCachedDurableThread(threadId);
      return;
    }

    // Known to hold nothing — re-fetching on every panel reopen would spend requests to learn the
    // same nothing. Threads WITH messages fall through to the cold load when the LRU cache has
    // evicted them, so this shortcut only ever applies to genuinely empty ones.
    if (this.knownEmptyThreadIds.has(threadId)) {
      this.chat.messages = [];
      this.syncSignalsFromAdapter();
      // On the durable path "empty" is only what storage said last time: another window may have
      // given the thread its first turn since, so it is re-read like a cached transcript.
      this.revalidateCachedDurableThread(threadId);
      return;
    }

    // A cold target must not inherit the previous thread's messages, including when its load fails.
    // Otherwise New chat can cache those old messages under the unavailable target's ID.
    this.chat.messages = [];
    this.syncSignalsFromAdapter();

    // A load that just failed stays failed for a moment. The caller's "no messages means load it"
    // rule would otherwise retry on every panel toggle, which turns a rate-limit rejection into a
    // stream of further rejections.
    const failure = this.failedThreadLoads.get(threadId);
    if (failure && Date.now() - failure.failedAt < THREAD_LOAD_RETRY_COOLDOWN_MS) {
      this._threadUnavailable.set(failure.unavailable);
      this._threadLoadError.set(failure.unavailable ? null : 'Could not load this conversation. Please try again.');
      return;
    }

    // Already being fetched. The foreground stays empty for as long as the request is in flight,
    // which is the same "no messages" condition the popup reads as "needs loading" — so without this
    // guard, toggling the panel during a slow or throttled response fires a duplicate load each time.
    // The pending request populates the view when it lands, so there is nothing to do but wait.
    if (untracked(() => this.loadingThreadIds()).has(threadId)) return;

    // Cold load: fetch from backend storage. The loading mark lasts exactly as long as the request,
    // whichever way it ends, so sends for this thread are held until its outcome is known.
    this.loadingThreadIds.update(ids => new Set(ids).add(threadId));
    this.aiService
      .getThreadMessages(AgentUtility.AGENT_ID, threadId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingThreadIds.update(ids => new Set([...ids].filter(id => id !== threadId)))),
      )
      .subscribe({
        next: (response) => {
          const messages = AgentUtility.convertToUIMessages(response.uiMessages ?? []);
          this.failedThreadLoads.delete(threadId);
          if (messages.length > 0) {
            this.knownEmptyThreadIds.delete(threadId);
            this.cacheThreadMessages(threadId, messages);
          } else {
            this.knownEmptyThreadIds.add(threadId);
          }

          // Record the result above regardless, but only paint it if this is still the open thread —
          // a slow response for a thread the user has since left must not overwrite what they are
          // now looking at.
          if (this.$threadId() !== threadId) return;
          this.chat.messages = messages;
          this.syncSignalsFromAdapter();
          this.attachDurable(threadId);
        },
        error: (err) => {
          console.error('[AgentChat] Failed to load thread messages:', err);
          const unavailable = err instanceof HttpErrorResponse && (err.status === 403 || err.status === 404);
          this.failedThreadLoads.set(threadId, {
            failedAt: Date.now(),
            unavailable,
          });
          if (this.$threadId() !== threadId) return;
          this._threadUnavailable.set(unavailable);
          this._threadLoadError.set(unavailable ? null : 'Could not load this conversation. Please try again.');
        },
      });
  }

  /**
   * Start a new empty thread, caching the current one.
   */
  public async createNewThread(): Promise<void> {

    this.threadSwitches += 1;
    // A first thread still being created belongs to the chat being left; a send in the next chat must not join it.
    this.pendingThreadCreation = null;
    this._threadLoadError.set(null);
    this._threadUnavailable.set(false);

    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this.detachCurrentStream();
    this.chat.resetTransientState();

    // Save current thread's live messages before starting fresh
    const prevThreadId = this.$threadId();
    if (prevThreadId && this.chat.messages.length > 0) {
      this.cacheThreadMessages(prevThreadId, this.chat.messages);
    }

    this.$threadId.set(null);
    this.chat.messages = [];
    this.$messages.set([]);
    this.$allSessionBlobUrls.set({});
    this.$cardActions.set({});
    // The new chat shares the draft of the thread you came from, so what you were typing carries
    // into it; other threads keep their own. A truly fresh new chat (no prior thread) starts empty.
    this.panelService.setComposerDraft(NEW_CHAT_DRAFT_KEY, prevThreadId ? this.panelService.composerDraftFor(prevThreadId) : '');
    // New empty chat: drop any thread-specific tier so the composer unlocks and follows the user's default.
    this.$activeThreadModelTier.set(null);
  }

  // ─── Tool Approvals (HITL) ─────────────────────────────────────────────────

  /**
   * Approve a tool call by its approval ID. For the `interview` tool, pass the
   * structured card answers as `interviewResponse` so the resumed execute can
   * return them as the tool result instead of the model re-asking.
   * Resolves `false` when the approval could not be dispatched (e.g. its run id
   * is unresolvable after a restore), in which case an error is surfaced for retry.
   */
  public async approveToolCall(toolCallId: string, interviewResponse?: AIInterviewResponse): Promise<boolean> {

    return this.chat.approveToolCall(toolCallId, interviewResponse);
  }

  /**
   * Decline a tool call by its approval ID with an optional reason. Resolves `false` when the
   * approval could not be dispatched (e.g. its run id is unresolvable after a restore).
   */
  public async declineToolCall(toolCallId: string, reason?: string): Promise<boolean> {

    return this.chat.declineToolCall(toolCallId, reason);
  }

  /**
   * Record a card action (approved/declined/selected/skipped) against the tool part.
   *
   * In-memory only. The card resumes through the approve path, which drives the tool part to
   * output-available (or output-denied) with the answers on it — and that stored part, not this
   * metadata, is what the message list reads back when a thread is reloaded. Writing the same
   * outcome to the server as well cost a second request per card and told us nothing new.
   *
   * The local write still matters for the live window before the resumed output lands: it gates the
   * card overlay so an answered card retires immediately, and it supplies the positional fallback
   * recap until the real output arrives.
   */
  public persistCardAction(messageId: string, toolCallId: string, meta: InterviewCardActionMeta): void {

    this.$cardActions.update(prev => ({ ...prev, [toolCallId]: meta }));

    const message = this.chat.messages.find(m => m.id === messageId && m.role === 'assistant');
    if (!message) return;

    const partIndex = message.parts.findIndex(p => AgentUtility.partField(p, 'toolCallId', '') === toolCallId);
    if (partIndex === -1) return;

    const originalPart = message.parts[partIndex] as Record<string, unknown>;
    const existing = (originalPart['callProviderMetadata'] as Record<string, unknown>) ?? {};
    message.parts[partIndex] = { ...originalPart, callProviderMetadata: { ...existing, workbuddy: { cardAction: meta.action, timestamp: meta.timestamp, answers: meta.answers } } } as unknown as typeof message.parts[number];
  }

  // ─── Reasoning State ───────────────────────────────────────────────────────

  /**
   * Toggle manual expand/collapse of an activity block.
   */
  public toggleActivityBlock(blockKey: string): void {

    const isCurrentlyCollapsed = this.$collapsedActivityBlocks().has(blockKey);
    if (isCurrentlyCollapsed) {
      // User is expanding — track it so auto-collapse won't override
      this.manuallyExpandedBlocks.add(blockKey);
    } else {
      // User is collapsing — remove from manual tracking
      this.manuallyExpandedBlocks.delete(blockKey);
    }
    this.$collapsedActivityBlocks.update(prev => {
      const next = new Set(prev);
      if (next.has(blockKey)) {
        next.delete(blockKey);
      } else {
        next.add(blockKey);
      }
      return next;
    });
  }

  // ─── Tool Expand State ─────────────────────────────────────────────────────

  /**
   * Toggle expand/collapse of a tool call result section.
   */
  public toggleToolExpand(callId: string): void {

    this._expandedTools.update(prev => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  }

  // ─── Error Management ─────────────────────────────────────────────────────

  /**
   * Clear all error states.
   */
  public clearErrors(): void {

    this.$streamingError.set(null);
    this._threadLoadError.set(null);
    this.chat?.clearError();
  }

  /**
   * Retry the last user message after a streaming error.
   * Clears the error, removes the failed user/assistant messages, and re-sends.
   *
   * On the durable path a REFUSED send is its own record: the request never reached the server and
   * its pair is already off the screen, so there is nothing to truncate and the last user message
   * still visible is the PREVIOUS, already answered request. Retry sends the refused request, never
   * that one, and the completed history behind it is left exactly as it is.
   */
  public async retryLastMessage(): Promise<void> {

    const durable = this.durableChat;
    const failed = this.failedSend;
    if (durable && failed) {
      durable.clearFailedSend();
      this.$streamingError.set(null);
      durable.clearError();
      // The same request again: its words and any file that went with it.
      if (failed.text || failed.files.length > 0) await this.sendMessage(failed.text, failed.files);
      return;
    }

    const messages = this.$messages();
    const lastUserIndex = messages.map(m => m.role).lastIndexOf('user');
    if (lastUserIndex === -1) return;

    const lastUserMsg = messages[lastUserIndex];

    // Extract text from the user message parts
    const text = lastUserMsg.parts.filter((p): p is { type: 'text'; text: string } => p.type === 'text').map(p => p.text).join('\n');
    if (!text) return;

    // Remove the last user message and any subsequent messages (failed assistant response)
    // to avoid duplicates when sendMessage adds the user message back
    this.chat.messages = messages.slice(0, lastUserIndex);

    // Clear the streaming error and re-send
    this.$streamingError.set(null);
    this.chat?.clearError();
    await this.sendMessage(text);
  }

  // ─── Private: Effects ──────────────────────────────────────────────────────

  /**
   * Auto-collapse activity blocks when a text part appears after them.
   */
  private setupAutoCollapseActivityBlocksEffect(): void {

    effect(() => {

      const chat = this.$chat();
      const _status = chat.status;
      const keysToCollapse = new Set<string>();
      const messages = chat.messages;
      const lastMessage = messages[messages.length - 1];

      for (const msg of messages) {
        if (msg.role !== 'assistant') continue;
        // A block the agent has moved past collapses: text landed after it, or the message it sits on
        // is no longer the last one (a steering message the loop wove in now follows it). A queued
        // steering bubble is not in this list, so queuing alone never collapses the live block.
        const movedPast = msg !== lastMessage;
        const items = AgentUtility.groupMessageParts(msg.parts as AnyPart[]);
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind !== 'activity-block') continue;
          const blockKey = AgentUtility.activityBlockKey(msg.id, item.blockIndex);
          const hasTextAfter = items.slice(i + 1).some(it => it.kind === 'text');
          if (hasTextAfter || movedPast) keysToCollapse.add(blockKey);
        }
      }

      if (keysToCollapse.size > 0) {
        this.$collapsedActivityBlocks.update(prev => {
          const next = new Set(prev);
          let changed = false;
          for (const k of keysToCollapse) {
            if (!next.has(k) && !this.manuallyExpandedBlocks.has(k)) { next.add(k); changed = true; }
          }
          return changed ? next : prev;
        });
      }
    }, { injector: this.injector });
  }

  /**
   * In popup mode, create a tab as soon as the AI starts streaming a response.
   * This gives immediate visual feedback without waiting for the server to generate a title.
   */
  private setupProvisionalTabEffect(): void {

    effect(() => {
      const streaming = this.$isStreaming();
      if (!streaming || this.$mode() !== 'popup') return;

      // Tracked: a new durable chat streams (its first message is painted as submitted) before its
      // thread exists, and the id it is published under later is what this effect must re-run for.
      const threadId = this.$threadId();
      if (!threadId) return;

      const hasTab = untracked(() => this.panelService.$openTabs().some(t => t.threadId === threadId));
      if (hasTab) return;

      const firstMsg = untracked(() => this.chat.messages.find(m => m.role === 'user'));
      const text = firstMsg?.parts?.find((p: unknown) => (p as { type: string }).type === 'text') as { text?: string } | undefined;
      const title = AgentUtility.provisionalThreadTitle(text?.text) || 'New chat';
      // The thread id can be published after the user minimised or closed the popup (a new durable
      // chat is created first); the tab is then collected without opening the panel again.
      if (untracked(() => this.panelService.$panelOpen())) this.panelService.addTab(threadId, title);
      else this.panelService.collectTab(threadId, title);
    }, { injector: this.injector });
  }

  /**
   * Poll for a new chat's generated title when its turn really ends. A first turn paused on an interview is not
   * titled until the answer's resumed turn finishes, so polling at the pause would run out of attempts first.
   * Covers the first turn, a resumed turn, and a turn that finishes after the user came back to the chat mid-stream.
   */
  private setupTitleFetchOnTurnEndEffect(): void {

    effect(() => {

      // Judged on the status the effect lands on, not on having watched the run start: a short reply can go from
      // submitted to idle before this effect runs, and waiting to see "running" would then miss the turn's end.
      const chat = this.$chat();
      const turnFinished = AgentChatService.isTurnFinished(chat);
      untracked(() => {
        const threadId = this.$threadId();
        if (turnFinished && threadId && this.panelService.isTitlePending(threadId)) this.fetchThreadTitle(threadId);
      });
    }, { injector: this.injector });
  }

  /** True when a turn has neither a run in flight nor an interview or approval waiting on the user. */
  private static isTurnFinished(chat: AgentChatAdapter): boolean {

    const running = chat.status === 'streaming' || chat.status === 'submitted';
    return !running && !chat.hasPendingApproval;
  }

  /**
   * Create a new adapter instance.
   */
  private createAdapter(): AgentChatAdapter {

    if (this.durableEnabled) {
      return new DurableWorkbuddyAgentChatAdapter(this.durableTransport, this.aiService);
    }
    return new NativeWorkbuddyAgentChatAdapter(this.aiService);
  }

  /**
   * Fetch thread title after a new chat's turn ends, and again when returning to a chat whose title is still pending.
   * Mastra's generateTitle saves to storage but doesn't emit stream events,
   * so we fetch the title after a delay to allow the async generation to complete.
   */
  private fetchThreadTitle(threadId: string, attemptsLeft: number = TITLE_FETCH_MAX_ATTEMPTS): void {

    // One poll at a time: a turn end and a return to the chat can both ask, and the newer request replaces the older.
    if (this.titleFetchTimer) clearTimeout(this.titleFetchTimer);
    this.titleFetchTimer = setTimeout(async () => {
      // generateTitle is an async LLM call with no stream event and can take longer than one delay.
      // Poll until the title lands so the side panel updates live instead of only after a page refresh.
      // Stale fetch guard — user moved to another thread. The chat stays pending, so returning to it re-polls.
      if (this.$threadId() !== threadId) return;

      let title: string | undefined;
      try {
        title = (await firstValueFrom(this.aiService.getThread(AgentUtility.AGENT_ID, threadId))).title;
      } catch {
        // A failed request is treated like "not generated yet"; the next attempt asks again.
      }

      // Checked again after the request: the user may have left or deleted the chat while it was in flight, and
      // publishing then would put a deleted chat back in the sidebar. A chat left this way stays pending.
      if (this.$threadId() !== threadId) return;

      if (title) {
        this.panelService.clearTitlePending(threadId);
        if (this.$mode() === 'popup') {
          this.panelService.updateTabTitle(threadId, title);
        }
        this.$threadTitleUpdate.set({ threadId, title });
        return;
      }

      if (attemptsLeft > 1) this.fetchThreadTitle(threadId, attemptsLeft - 1);
      // Out of attempts: generation most likely failed, so stop re-polling this chat on every return.
      else this.panelService.clearTitlePending(threadId);
    }, TITLE_FETCH_DELAY_MS);
  }

  /**
   * Read the model tier the given thread was started on (from its stored metadata) and publish it
   * as $activeThreadModelTier so the composer can lock to it. A pre-feature thread (or any thread
   * with no recorded tier) leaves the signal null, so the composer falls back to the user's default.
   * Stale-guarded: a fetch that resolves after the user has switched threads is discarded.
   */
  private loadActiveThreadModelTier(threadId: string): void {

    // Cache hit: publish synchronously, no network call. This is the key guard that stops
    // rapid chat switching from firing a redundant getThread per click (the 429 storm).
    if (this.threadModelTierCache.has(threadId)) {
      this.$activeThreadModelTier.set(this.threadModelTierCache.get(threadId) ?? null);
      return;
    }

    void this.resolveThreadModelTier(threadId).then(tier => {
      if (this.$threadId() !== threadId) return; // Stale fetch guard — user moved to another thread
      this.$activeThreadModelTier.set(tier);
    });
  }

  /**
   * Resolve the authoritative model tier an existing thread was started on, awaiting the lookup on a
   * cache miss. Used at send time so a message can never go out on the composer's transient 'default'
   * during the cold switch/restore window before the tier has loaded. Returns null for a pre-feature
   * thread (no stored tier), letting the caller fall back to the composer value.
   *
   * Concurrent callers share one request: the cache is only written when a response lands, so a send
   * issued while the switch's lookup is still in flight would otherwise fire a duplicate GET.
   */
  private resolveThreadModelTier(threadId: string): Promise<AgentModelTier | null> {

    if (this.threadModelTierCache.has(threadId)) {
      return Promise.resolve(this.threadModelTierCache.get(threadId) ?? null);
    }

    const existing = this.threadModelTierRequests.get(threadId);
    if (existing) return existing;

    const request = firstValueFrom(this.aiService.getThread(AgentUtility.AGENT_ID, threadId))
      .then(thread => {
        const tier = coerceThreadModelTier(thread.metadata?.['modelTier']);
        this.threadModelTierCache.set(threadId, tier);
        return tier;
      })
      // Non-fatal: if the thread can't be read, leave the tier unresolved and fall back to default.
      .catch(() => null)
      .finally(() => this.threadModelTierRequests.delete(threadId));

    this.threadModelTierRequests.set(threadId, request);
    return request;
  }

  // ─── Private: Helpers ──────────────────────────────────────────────────────

  /**
   * Cache thread messages with LRU eviction.
   * Keeps at most MAX_CACHED_THREADS entries, evicting the least recently used.
   */
  private cacheThreadMessages(threadId: string, messages: UIMessage[]): void {

    // Remove from current position in order (if exists)
    const idx = this.threadCacheOrder.indexOf(threadId);
    if (idx !== -1) this.threadCacheOrder.splice(idx, 1);

    // Add to end (most recently used)
    this.threadCacheOrder.push(threadId);
    this.threadMessageCache.set(threadId, messages);

    // Evict oldest if over limit
    while (this.threadCacheOrder.length > MAX_CACHED_THREADS) {
      const evictId = this.threadCacheOrder.shift()!;
      this.threadMessageCache.delete(evictId);
    }
  }

  /** Drops a thread's cached transcript so the next switch to it reads storage. */
  private forgetCachedThread(threadId: string): void {

    this.threadMessageCache.delete(threadId);
    const index = this.threadCacheOrder.indexOf(threadId);
    if (index !== -1) this.threadCacheOrder.splice(index, 1);
  }

  /**
   * Rebuilds all thread-level derived state from loaded messages in a single pass:
   * card action states (from callProviderMetadata) and collapsed thinking block keys.
   */
  private rebuildThreadState(): void {

    const cardActions: Record<string, InterviewCardActionMeta> = {};

    for (const message of this.chat.messages) {
      for (let i = 0; i < message.parts.length; i++) {
        const part = message.parts[i];

        if (message.role !== 'assistant') continue;

        const toolCallId = AgentUtility.partField(part, 'toolCallId', '');
        if (toolCallId) {
          const providerMeta = AgentUtility.partField<Record<string, unknown> | null>(part, 'callProviderMetadata', null);
          const wbMeta = providerMeta?.workbuddy as { cardAction?: string; timestamp?: number; answers?: AIInterviewAnswer[] } | undefined;
          if (wbMeta?.cardAction) {
            cardActions[toolCallId] = {
              action: wbMeta.cardAction as CardAction,
              timestamp: wbMeta.timestamp ?? 0,
              answers: wbMeta.answers ?? [],
            };
          }
        }
      }
    }

    // Collapse blocks the agent has moved beyond, mirroring the live auto-collapse
    // effect's rule. The trailing block of the last message stays OPEN while the
    // stream is active (it is still being streamed into) — without this, reattaching
    // a backgrounded stream (popup reopen, nav back to /agent) collapses the live
    // block mid-turn. Manual expand/collapse choices are preserved across the rebuild.
    const messages = this.chat.messages;
    const lastMessage = messages[messages.length - 1];
    const streaming = this.$isStreaming();
    const previouslyCollapsed = this.$collapsedActivityBlocks();
    const blockKeys = new Set<string>();
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      const items = AgentUtility.groupMessageParts(message.parts as AnyPart[]);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'activity-block') continue;
        const blockKey = AgentUtility.activityBlockKey(message.id, item.blockIndex);
        if (this.manuallyExpandedBlocks.has(blockKey)) continue;
        const hasTextAfter = items.slice(i + 1).some(it => it.kind === 'text');
        const isLiveTrailingBlock = streaming && message === lastMessage && !hasTextAfter;
        // A live block the user explicitly collapsed stays collapsed (key is in the previous set).
        if (isLiveTrailingBlock && !previouslyCollapsed.has(blockKey)) continue;
        blockKeys.add(blockKey);
      }
    }

    this.$cardActions.set(cardActions);
    if (blockKeys.size > 0) {
      this.$collapsedActivityBlocks.set(blockKeys);
    }
  }
}
